import { useState } from 'react';

interface DataPreviewProps {
  data: any[];
  headers: string[];
  validation?: {
    errors: any[];
    total: number;
    validCount: number;
  };
  isLoading?: boolean;
}

export function DataPreview({ data, headers, validation, isLoading = false }: DataPreviewProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-3 text-gray-600 text-sm">Procesando datos...</p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No hay datos para mostrar
      </div>
    );
  }

  const totalPages = Math.ceil(data.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, data.length);
  const currentData = data.slice(startIndex, endIndex);

  return (
    <div className="space-y-4">
      {/* Resumen de validación */}
      {validation && (
        <div className="table-header">
          <div className="table-header-title">
            <span>📋 Resumen de validación</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="badge badge-neutral">
              Total: <strong>{validation.total}</strong>
            </span>
            <span className="badge badge-success">
              Válidos: <strong>{validation.validCount}</strong>
            </span>
            {(validation.errors?.length || 0) > 0 && (
              <span className="badge badge-error">
                Errores: <strong>{validation.errors.length}</strong>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Tabla */}
      <div className="table-container" style={{ maxHeight: '420px', overflow: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: '50px', textAlign: 'center' }}>#</th>
              {headers.map((header, index) => (
                <th key={index}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentData.map((row, rowIndex) => {
              const isInvalid = row._valid === false;
              return (
                <tr key={rowIndex} style={isInvalid ? { background: '#fef2f2' } : undefined}>
                  <td style={{ textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>
                    {startIndex + rowIndex + 1}
                  </td>
                  {headers.map((header, colIndex) => (
                    <td key={colIndex} style={{ maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row[header] !== undefined && row[header] !== null
                        ? String(row[header])
                        : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">
            Mostrando {startIndex + 1}–{endIndex} de {data.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="btn btn-ghost btn-sm"
            >
              ← Anterior
            </button>
            <span className="px-3 py-1 text-gray-700 font-medium">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="btn btn-ghost btn-sm"
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}